"use client";

import React from "react";
import _ from "lodash";
import { format, addDays, startOfMonth } from "date-fns";

interface HeavyTableProps {
  title?: string;
}

const HeavyTable: React.FC<HeavyTableProps> = ({
  title = "Heavy Data Table",
}) => {
  // Generate a large dataset using lodash
  const generateTableData = () => {
    const startDate = startOfMonth(new Date());

    return _.range(0, 30).map((i) => {
      const date = addDays(startDate, i);
      return {
        id: i + 1,
        date: format(date, "yyyy-MM-dd"),
        name: `User ${_.padStart((i + 1).toString(), 3, "0")}`,
        email: `user${i + 1}@example.com`,
        revenue: _.round(_.random(100, 10000, true), 2),
        status: _.sample(["Active", "Inactive", "Pending"]) as string,
        score: _.random(1, 100),
      };
    });
  };

  const data = generateTableData();
  const totalRevenue = _.sumBy(data, "revenue");
  const averageScore = _.round(_.meanBy(data, "score"), 1);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">{title}</h3>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div className="bg-blue-50 p-3 rounded">
          <p className="text-sm text-gray-600">Total Revenue</p>
          <p className="text-xl font-bold text-blue-600">
            ${totalRevenue.toLocaleString()}
          </p>
        </div>
        <div className="bg-green-50 p-3 rounded">
          <p className="text-sm text-gray-600">Average Score</p>
          <p className="text-xl font-bold text-green-600">{averageScore}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">ID</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Revenue</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Score</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-2">{row.id}</td>
                <td className="px-4 py-2">{row.date}</td>
                <td className="px-4 py-2 font-medium">{row.name}</td>
                <td className="px-4 py-2 text-gray-600">{row.email}</td>
                <td className="px-4 py-2 text-green-600">
                  ${row.revenue.toLocaleString()}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      row.status === "Active"
                        ? "bg-green-100 text-green-800"
                        : row.status === "Inactive"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {row.status}
                  </span>
                </td>
                <td className="px-4 py-2">{row.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-sm text-gray-600 mt-4">
        This component uses Lodash for data manipulation and date-fns for date
        formatting
      </p>
    </div>
  );
};

export default HeavyTable;
